import { Component, EventEmitter, Input, Output, OnChanges, OnDestroy } from '@angular/core';
import { Event } from '../../shared/models/event';
import { PlayerEventInfo, hasAssignedTeams } from '../../shared/models/playerEventInfo';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { EventService } from '../../shared/services/event.service';
import { DelayedSpinnerService } from '../../shared/services/delayed-spinner.service';
import { managerInviteOptions, ManagerInviteOption } from './manager-invite-options';
import { Chat } from '../../shared/models/chat';

@Component({
  selector: 'app-match-home',
  templateUrl: './match-home.component.html',
  styleUrls: ['./match-home.component.css'],
  standalone: false
})
export class MatchHomeComponent implements OnChanges, OnDestroy {
  constructor(private events: EventService, private spinner: DelayedSpinnerService) {}
  private destroyed$ = new Subject<void>();
  groupInviteOpen = false;
  selectedInvite: ManagerInviteOption = null;
  sendingGroupInvite = false;
  groupInviteMessage = '';
  groupInviteError = false;
  groupInviteSentCount = 0;
  trackInviteOption(_index: number, option: ManagerInviteOption): string { return option.type; }
  get inviteOptions(): ManagerInviteOption[] { return managerInviteOptions(this.event); }
  openGroupInvites(): void {
    if (this.busy || this.sendingGroupInvite || !this.inviteOptions.length) return;
    this.selectedInvite = this.inviteOptions.length === 1 ? this.inviteOptions[0] : null;
    this.groupInviteMessage = '';
    this.groupInviteError = false;
    this.groupInviteSentCount = 0;
    this.groupInviteOpen = true;
  }
  sendGroupInvite(): void {
    if (this.busy || this.sendingGroupInvite || !this.selectedInvite) return;
    const option = this.inviteOptions.find(item => item.type === this.selectedInvite.type);
    if (!option) { this.selectedInvite = null; return; }
    const eventId = this.event.id;
    this.sendingGroupInvite = true;
    this.groupInviteMessage = '';
    const finish = this.spinner.acquire();
    const request = option.type === 'REMINDER' ? this.events.notifyNotAnsweredEventPlayers(eventId)
      : this.events.notifyPlayers(this.event, option.type === 'ALL' ? null : option.type);
    request.pipe(takeUntil(this.destroyed$), finalize(() => { this.sendingGroupInvite = false; finish(); })).subscribe({
      next: result => {
        if (this.event.id !== eventId) return;
        this.selectedInvite = null;
        this.groupInviteSentCount = result?.sentCount || 0;
        this.groupInviteError = false;
        this.groupInviteMessage = this.groupInviteSentCount > 0 ? 'notifications.sent' : 'notifications.invitationLimitReached';
        this.action.emit('invited');
      },
      error: () => { this.groupInviteError = true; this.groupInviteMessage = 'errors.generic'; }
    });
  }
  ngOnDestroy(): void { this.destroyed$.next(); this.destroyed$.complete(); }

  @Input() event: Event;
  @Input() chat: Chat;
  @Input() busy = false;
  @Input() detail = false;
  @Output() action = new EventEmitter<string>();
  @Output() attendance = new EventEmitter<string>();
  @Output() teamSaving = new EventEmitter<boolean>();
  @Output() player = new EventEmitter<PlayerEventInfo>();
  @Output() candidate = new EventEmitter<{player: PlayerEventInfo; decision: string}>();
  get candidates(): PlayerEventInfo[] { return this.members.filter(p => p.teamMemberType === 'CANDIDATE'); }
  selectedStatus = 'CONFIRMED';
  expanded = false;
  search = '';
  responseOpen = false;
  inviteOpen = false;
  chooseResponse(status: string): void {
    if (!this.canRespond || this.busy) return;
    this.responseOpen = false;
    if (status !== this.event.currentPlayerInviteStatus) this.attendance.emit(status);
  }
  get teamsAssigned(): boolean {
    return hasAssignedTeams(this.members);
  }
  private displayedEventId: string;
  ngOnChanges(): void {
    if (this.displayedEventId !== this.event?.id) {
      this.displayedEventId = this.event?.id;
      this.groupInviteOpen = false;
      this.selectedInvite = null;
      this.selectedStatus = 'CONFIRMED';
      this.expanded = false;
      this.search = '';
    }
  }
  selectStatus(status: string): void {
    this.selectedStatus = status;
    this.expanded = false;
    this.search = '';
  }
  get attendanceTitle(): string {
    if (!this.event.viewedByManager) return 'events.comingSectionTitle';
    return ({CONFIRMED: 'events.managerAttendanceTitle', DECLINED: 'events.notComingSectionTitle',
      NOT_ANSWERED: 'events.notAnswered', NOT_NOTIFIED: 'events.notInvited'})[this.selectedStatus];
  }
  get attendanceCount(): number { return this.selectedStatus === 'CONFIRMED' ? this.confirmed : this.count(this.selectedStatus); }
  get searchEnabled(): boolean {
    return this.event.viewedByManager && ['NOT_ANSWERED', 'NOT_NOTIFIED'].includes(this.selectedStatus) && this.count(this.selectedStatus) > 10;
  }
  get previewLimit(): number { return this.member ? 10 : 6; }
  get hiddenPlayerCount(): number { return Math.max(0, this.filteredPlayers.length - this.previewLimit); }
  get canJoin(): boolean { return this.available && !this.event.incompleteSetup && !this.event.viewedByCandidate && !this.member && this.missing > 0 && (this.event.access?.canRequestJoin ?? this.event.openToPublic); }
  get emptyKey(): string {
    return ({CONFIRMED: 'events.rosterEmptyConfirmed', DECLINED: 'events.rosterEmptyDeclined',
      NOT_ANSWERED: 'events.rosterEmptyNotAnswered', NOT_NOTIFIED: 'events.rosterEmptyNotNotified'})[this.selectedStatus];
  }
  readonly statuses = [
    { value: 'CONFIRMED', key: 'guestOwner.coming', icon: 'common.check' },
    { value: 'DECLINED', key: 'guestOwner.notComing', icon: 'common.close' },
    { value: 'NOT_ANSWERED', key: 'events.notAnswered', icon: 'match.status.notAnswered' },
    { value: 'NOT_NOTIFIED', key: 'events.notInvited', icon: 'match.status.notNotified' }
  ];
  get members(): PlayerEventInfo[] { return this.event?.playerEventInfos || []; }
  get confirmed(): number { return this.event.attendance?.confirmed ?? this.event.confirmedCount ?? this.members.filter(p => p.teamMemberType !== 'CANDIDATE' && p.status === 'CONFIRMED').length; }
  get missing(): number { return Math.max(0, (this.event.maxPlayers || 0) - this.confirmed); }
  get canRespond(): boolean { return this.available && !this.event.incompleteSetup && this.event.viewedByPlayer && (['NOT_ANSWERED', 'CONFIRMED', 'DECLINED'].includes(this.event.currentPlayerInviteStatus) || this.event.viewedByManager && this.event.currentPlayerInviteStatus === 'NOT_NOTIFIED'); }
  get available(): boolean { return this.event.active !== false && !this.event.cancelled; }
  get member(): boolean { return this.event.viewedByManager || this.event.viewedByPlayer; }
  get progress(): number { return this.event.maxPlayers > 0 ? Math.min(100, this.confirmed / this.event.maxPlayers * 100) : 0; }
  get attendanceTone(): string {
    if (!(this.event.maxPlayers > 0)) return 'unset';
    if (this.progress < 40) return 'low';
    if (this.progress < 70) return 'building';
    return this.progress < 100 ? 'almost' : 'full';
  }
  get changes(): number { return this.event.managerHome?.importantChanges?.length || 0; }
  get location(): string { return this.event.playSpace?.name || this.event.locationName || ''; }
  get lastMessage(): string {
    const message = this.chat?.messages?.filter(item => !item.deleted && !item.inappropriate).slice().sort((a, b) => Number(b.created) - Number(a.created))[0];
    return message && !message.deleted && !message.inappropriate ? message.text : '';
  }
  get ownStatusKey(): string {
    if (this.event.cancelled) return 'events.cancelled';
    if (this.event.viewedByCandidate) return 'events.joinPending';
    return ({ CONFIRMED: 'events.myComingStatus', DECLINED: 'events.myNotComingStatus', NOT_ANSWERED: 'events.myRsvpQuestion', NOT_NOTIFIED: this.event.viewedByManager ? 'events.myRsvpQuestion' : 'events.youAreNotInvited' })[this.event.currentPlayerInviteStatus] || 'events.openMatch';
  }
  private playersForStatus(status: string): PlayerEventInfo[] {
    return this.members.filter(p => p.teamMemberType !== 'CANDIDATE' && p.status === status);
  }
  count(status: string): number {
    if (status === 'CANDIDATE') return this.candidates.length;
    return this.playersForStatus(status).length;
  }
  get filteredPlayers(): PlayerEventInfo[] {
    const players = this.playersForStatus(this.event.viewedByManager ? this.selectedStatus : 'CONFIRMED');
    const query = this.searchEnabled ? this.search.trim().toLocaleLowerCase() : '';
    return query ? players.filter(p => [p.player?.nickName, p.player?.name].some(value => value?.toLocaleLowerCase().includes(query))) : players;
  }
  get visiblePlayers(): PlayerEventInfo[] {
    return this.expanded ? this.filteredPlayers : this.filteredPlayers.slice(0, this.previewLimit);
  }
  initials(p: PlayerEventInfo): string {
    return (p.player?.nickName || p.player?.name || '?').split(/[\s._-]+/).filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase();
  }
}
